import React from 'react';
import { Accordion, Form } from 'react-bootstrap';
import { PermissionResponse } from '../types/types';
import { STANDARD_ACTIONS } from '../utils';

interface PermissionsAccordionProps {
  permissionsByCategory: Record<string, any>;
  field: {
    value: PermissionResponse[];
    onChange: (value: PermissionResponse[]) => void;
  };
  isLoading: boolean;
}

const pid = (p: any): string => p?.id || p?._id || '';

const PermissionsAccordion: React.FC<PermissionsAccordionProps> = ({
  permissionsByCategory,
  field,
  isLoading
}) => {
  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading permissions...</span>
        </div>
        <p className="mt-2">Loading permissions...</p>
      </div>
    );
  }

  return (
    <Accordion defaultActiveKey="0">
      {Object.entries(permissionsByCategory || {}).map(
        ([categoryKey, categoryData]: [string, any], categoryIndex) => (
          <Accordion.Item eventKey={String(categoryIndex)} key={categoryKey}>
            <Accordion.Header>
              <span className="text-capitalize">
                {categoryData?._category?.charAt(0)?.toUpperCase() +
                  categoryData?._category?.slice(1)}
              </span>
            </Accordion.Header>
            <Accordion.Body>
              {categoryData?._permissions &&
                categoryData?._permissions.length > 0 && (
                  <div className="mb-3">
                    <h6>General</h6>
                    <div className="d-flex flex-wrap gap-3">
                      {STANDARD_ACTIONS.map(action => {
                        const permission = categoryData?._permissions?.find(
                          (p: any) => p?.action === action
                        );
                        return (
                          permission && (
                            <Form.Check
                              key={pid(permission)}
                              type="checkbox"
                              label={
                                action?.charAt(0)?.toUpperCase() +
                                action?.slice(1)
                              }
                              checked={field.value?.some(
                                p => pid(p) === pid(permission)
                              )}
                              onChange={e => {
                                const updatedPermissions = e.target.checked
                                  ? [...field.value, permission]
                                  : field.value?.filter(
                                      p => pid(p) !== pid(permission)
                                    );
                                field.onChange(updatedPermissions);
                              }}
                            />
                          )
                        );
                      })}
                    </div>
                  </div>
                )}

              {categoryData?._subcategories &&
                Object.entries(categoryData?._subcategories).map(
                  ([subcategoryKey, subcategoryData]: [string, any]) => {
                    // Handle Active Jobs with job_stage nested subcategory
                    const isActiveJobs =
                      subcategoryKey === 'activejobs' &&
                      subcategoryData?._nestedSubcategories?.job_stage;

                    // Check if this subcategory has permissions in the standard format
                    const hasStandardPermissions =
                      Object.keys(subcategoryData?.permissions || {}).length >
                      0;

                    // Check if this subcategory has nested subcategories
                    const hasNestedSubcategories =
                      Object.keys(subcategoryData?._nestedSubcategories || {})
                        .length > 0;

                    return (
                      <div className="mb-3" key={subcategoryKey}>
                        <div className="d-flex flex-column flex-md-row gap-2 gap-md-5 align-items-md-center mb-2">
                          <h6 className="text-capitalize mb-0 me-md-2">
                            {subcategoryData?._subCategoryDisplayName ||
                              subcategoryKey}
                          </h6>

                          {!isActiveJobs && hasStandardPermissions && (
                            <div className="d-flex flex-wrap gap-3">
                              {STANDARD_ACTIONS.map(action => {
                                const permissionArray =
                                  subcategoryData?.permissions?.[action] || [];
                                const permission = permissionArray[0];
                                return (
                                  permission && (
                                    <Form.Check
                                      key={pid(permission)}
                                      type="checkbox"
                                      label={
                                        action?.charAt(0)?.toUpperCase() +
                                        action?.slice(1)
                                      }
                                      checked={field.value?.some(
                                        p => pid(p) === pid(permission)
                                      )}
                                      onChange={e => {
                                        const updatedPermissions = e.target
                                          .checked
                                          ? [...field.value, permission]
                                          : field.value?.filter(
                                              p => pid(p) !== pid(permission)
                                            );
                                        field.onChange(updatedPermissions);
                                      }}
                                    />
                                  )
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Render nested subcategories */}
                        {hasNestedSubcategories &&
                          Object.entries(
                            subcategoryData._nestedSubcategories
                          ).map(([nestedKey, nestedData]: [string, any]) => {
                            // For job_stage with stages array
                            if (
                              nestedKey === 'job_stage' &&
                              nestedData.stages
                            ) {
                              return (
                                <div className="ms-4 mb-3" key={nestedKey}>
                                  <h6 className="text-capitalize mb-2">
                                    {nestedData._nestedSubcategoryDisplayName ||
                                      'Job Stage'}
                                  </h6>
                                  <div className="d-flex flex-wrap gap-3 ms-3">
                                    {nestedData.stages.map((stage: any) => (
                                      <Form.Check
                                        key={stage.id}
                                        type="checkbox"
                                        label={stage.name}
                                        checked={field.value?.some(
                                          p => pid(p) === stage.id
                                        )}
                                        onChange={e => {
                                          const stagePermission = {
                                            _id: stage.id,
                                            id: stage.id,
                                            key: stage.key,
                                            name: stage.name,
                                            action: stage.name,
                                            isActive: stage.isActive
                                          };
                                          const updatedPermissions: any = e
                                            .target.checked
                                            ? [...field.value, stagePermission]
                                            : field.value?.filter(
                                                p => pid(p) !== stage.id
                                              );
                                          field.onChange(updatedPermissions);
                                        }}
                                        className="me-3"
                                      />
                                    ))}
                                  </div>
                                </div>
                              );
                            }

                            // For move_to_active and other nested subcategories with permissions
                            if (nestedData.permissions) {
                              const nestedPermissions: PermissionResponse[] =
                                [];

                              // Extract permissions from all action types
                              Object.entries(nestedData.permissions).forEach(
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                ([action, permArray]: [string, any]) => {
                                  if (
                                    Array.isArray(permArray) &&
                                    permArray.length > 0
                                  ) {
                                    nestedPermissions.push(...permArray);
                                  }
                                }
                              );

                              if (nestedPermissions.length > 0) {
                                return (
                                  <div className="ms-4 mb-3" key={nestedKey}>
                                    <h6 className="text-capitalize mb-2">
                                      {nestedData._nestedSubcategoryDisplayName ||
                                        nestedKey}
                                    </h6>
                                    <div className="d-flex flex-wrap gap-3 ms-3">
                                      {nestedPermissions.map(
                                        (permission: PermissionResponse) => {
                                          // Check by ID, key, and nestedSubcategory path
                                          const isChecked = field.value?.some(
                                            p => {
                                              // Try exact ID match first
                                              if (pid(p) === pid(permission))
                                                return true;

                                              // Try matching by key path
                                              if (
                                                p.key &&
                                                permission.key &&
                                                p.key === permission.key
                                              )
                                                return true;

                                              // Try matching by category path components
                                              if (
                                                p.category ===
                                                  permission.category &&
                                                p.subcategory ===
                                                  permission.subcategory &&
                                                p.nestedSubcategory ===
                                                  permission.nestedSubcategory &&
                                                p.action === permission.action
                                              ) {
                                                return true;
                                              }

                                              return false;
                                            }
                                          );

                                          return (
                                            <Form.Check
                                              key={pid(permission)}
                                              type="checkbox"
                                              label={
                                                permission.action
                                                  ?.charAt(0)
                                                  ?.toUpperCase() +
                                                permission.action?.slice(1)
                                              }
                                              checked={isChecked}
                                              onChange={e => {
                                                let updatedPermissions;
                                                if (e.target.checked) {
                                                  updatedPermissions = [
                                                    ...field.value,
                                                    permission
                                                  ];
                                                } else {
                                                  updatedPermissions =
                                                    field.value?.filter(p => {
                                                      // Remove by ID or key
                                                      if (
                                                        pid(p) ===
                                                        pid(permission)
                                                      )
                                                        return false;
                                                      if (
                                                        p.key === permission.key
                                                      )
                                                        return false;

                                                      // Remove by category path
                                                      if (
                                                        p.category ===
                                                          permission.category &&
                                                        p.subcategory ===
                                                          permission.subcategory &&
                                                        p.nestedSubcategory ===
                                                          permission.nestedSubcategory &&
                                                        p.action ===
                                                          permission.action
                                                      ) {
                                                        return false;
                                                      }

                                                      return true;
                                                    });
                                                }
                                                field.onChange(
                                                  updatedPermissions
                                                );
                                              }}
                                              className="me-3"
                                            />
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                            }

                            return null;
                          })}
                      </div>
                    );
                  }
                )}
            </Accordion.Body>
          </Accordion.Item>
        )
      )}
    </Accordion>
  );
};

export default PermissionsAccordion;
